/**
 * Post-processing of rule findings: grouping and deduplication ("probed 7
 * analytics globals", not 7 rows) and the Report's order
 * (docs/event-model.md §7). Owned by Phase 4 lane C.
 *
 * 1. Identical findings (same rule, severity, title, and target) merge, with
 *    their evidence and tags united.
 * 2. When one rule leaves `COLLAPSE_AT` or more findings of one non-critical
 *    severity, they collapse into a single row titled after the first
 *    (`Probed ga (and 4 more)`). Critical findings always stay separate.
 * 3. Most severe first, then by first evidence.
 */
import { severityRank } from "../events";
import type { Finding } from "./types";

/** How many same-rule, same-severity findings collapse into one row. */
export const COLLAPSE_AT = 4;

const firstEvidence = (f: Finding) => f.evidence[0] ?? Number.POSITIVE_INFINITY;
const unite = (a: readonly number[], b: readonly number[]) =>
  [...new Set([...a, ...b])].sort((x, y) => x - y);

function merge(into: Finding, other: Finding): Finding {
  const tags = [...new Set([...(into.tags ?? []), ...(other.tags ?? [])])];
  return {
    ...into,
    evidence: unite(into.evidence, other.evidence),
    ...(tags.length > 0 && { tags }),
  };
}

/** Groups in first-seen order. */
function groups(findings: readonly Finding[], key: (f: Finding) => string): Finding[][] {
  const map = new Map<string, Finding[]>();
  for (const f of findings) {
    const k = key(f);
    const group = map.get(k);
    if (group) group.push(f);
    else map.set(k, [f]);
  }
  return [...map.values()];
}

function collapse(group: Finding[]): Finding[] {
  const [first, ...rest] = group;
  if (!first || group.length < COLLAPSE_AT || first.severity === "critical") return group;
  const { target: _, ...head } = rest.reduce(merge, first);
  return [{ ...head, title: `${first.title} (and ${rest.length} more)` }];
}

/** Groups, deduplicates, and orders findings for the Report. */
export function finalizeFindings(findings: readonly Finding[]): Finding[] {
  const deduped = groups(findings, (f) =>
    JSON.stringify([f.rule, f.severity, f.title, f.target ?? null]),
  ).map((group) => group.slice(1).reduce(merge, group[0] as Finding));
  const collapsed = groups(deduped, (f) => `${f.rule}\n${f.severity}`).flatMap(collapse);
  return collapsed.sort(
    (a, b) =>
      severityRank(b.severity) - severityRank(a.severity) || firstEvidence(a) - firstEvidence(b),
  );
}
